import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import styles from './CommentActions.module.scss';
import Reactions from '../Reactions/Reactions';
import NameField from './NameField';
import ReactionPicker from './ReactionPicker';
import CmsAPI from '../../../../common/apis/CmsAPI';
import { useTranslation } from 'react-i18next';
import useAccessibleClick from '../../../../common/hooks/useAccessibleClick';

const CommentActions = ({
  storyId,
  user,
  comment,
  username,
  setUsername,
  storyAuthor,
  onCommentReplyClick,
  onCommentEditClick,
  setIsDelete,
  handleDeleteClick,
  handleResolvedClick,
  setAddReplyTo,
  isReply,
  setIsNewReactions,
}) => {
  const [reactions, setReactions] = useState(comment.reactions);
  const [newReaction, setNewReactions] = useState();
  const [selectedReactions, setSelectedReactions] = useState([]);
  const [showNameInputField, setShowNameInputField] = useState(false);
  const [cachedReaction, setCachedReaction] = useState({});
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [localStorageCommentReactions, setLocalStorageCommentReactions] = useState(JSON.parse(localStorage.getItem(comment.id)));
  const [reactionStatistics, setReactionStatistics] = useState([]);
  const [reactionStatisticsBar, setReactionStatisticsBar] = useState([]);

  const { t } = useTranslation();

  // We only allow deleting if the comment was created by a logged in user
  // and this is equal to the current logged in user or if current logged in user is an author of the video
  const isCanDeleteComment = useMemo(
    () => user?.username && (user.username === comment?.username || user.username === storyAuthor?.username),
    [user, storyAuthor, comment]
  );

  // We only allow editing if the comment was created by a logged in user
  // and this is equal to the current logged in user
  const isCanEditComment = useMemo(() => user?.username && user.username === comment?.username, [user, comment]);

  const isCanResolveComment = useMemo(() => comment.hasOwnProperty("position") && user?.username && user.username === comment?.username, [user, comment]);

  useEffect(() => {
    // Set users selected reactions
    let arr = [];
    reactions?.map(reaction => {
      if (
        reaction.username === user?.username ||
        (!user && reaction.authorName === username && localStorageCommentReactions?.some(unicode => unicode === reaction.unicode))
      ) {
        arr.push(reaction);
      }
    });
    setSelectedReactions(arr);
  }, [reactions, localStorageCommentReactions]);

  useEffect(() => {
    setReactions(comment.reactions);
  }, [comment]);

  useEffect(() => {
    if (showNameInputField) {
      setShowNameInputField(false);
      handleReaction(cachedReaction);
    }
  }, [username]);

  const handleReaction = reaction => {
    // function to handle the reaction click
    if (username || user?.username) {
      setIsNewReactions(true);
      if (selectedReactions?.some(item => item.unicode === reaction.unicode) && user) {
        // Delete if reaction exist, otherwise add new one
        handleDelete(reaction);
      } else if (user || (!user && !localStorageCommentReactions?.some(unicode => unicode === reaction.unicode))) {
        addReaction(reaction);
      }
    } else {
      // If not logged in and LocalStorage username is null
      setCachedReaction(reaction);
      setShowNameInputField(true);
      setAddReplyTo(null);
    }
  };

  const handleDelete = obj => {
    reactions?.map(reaction => {
      if (reaction.username === user?.username && obj.unicode === reaction.unicode) {
        setReactions(reactions => reactions.filter(item => item.id !== reaction.id));
        setSelectedReactions(reactions => reactions.filter(item => item.id !== reaction.id));
        deleteReaction(reaction);
      }
    });
  };

  const deleteReaction = async reaction => {
    await CmsAPI.deleteReaction(reaction.id).catch(e => {
      console.log(e);
    });
  };

  const addReaction = async reaction => {
    await CmsAPI.insertReaction({
      storyId: storyId,
      commentId: comment.id,
      username: user ? user.username : null,
      authorName: user ? user.userFullName : username,
      unicode: reaction.unicode,
    })
      .then(response => updateReactions(response))
      .catch(e => {
        console.log(e);
      });
  };

  const updateReactions = response => {
    setNewReactions(response.result.unicode);
    setReactions([...reactions, response.result]);
    if (localStorageCommentReactions) {
      setLocalStorageCommentReactions([...localStorageCommentReactions, response.result.unicode]);
    } else {
      setLocalStorageCommentReactions([response.result.unicode]);
    }
  };

  useEffect(() => {
    if (localStorageCommentReactions) {
      localStorage.setItem(comment.id, JSON.stringify(localStorageCommentReactions));
    }
  }, [localStorageCommentReactions]);

  const onAddReplyFormTo = () => {
    setIsDelete(false);
    setShowNameInputField(false);
    onCommentReplyClick(comment, isReply);
  };

  const onEditComment = () => {
    onCommentEditClick(comment);
  };

  const onDeleteClick = () => {
    setIsDelete(true);
    handleDeleteClick();
  };

  const deleteRef = useAccessibleClick(onDeleteClick);
  const editRef = useAccessibleClick(onEditComment);
  const replyRef = useAccessibleClick(onAddReplyFormTo);

  useEffect(() => {
    createStatistics();
  }, [selectedReactions, newReaction]);

  const createStatistics = () => {
    let arr = [];
    let counter = 0;

    reactions?.map(reaction => {
      let obj = { unicode: '', amount: 0, selected: false };
      if (!arr.some(e => e.unicode === reaction.unicode)) {
        // Check if the emoji already exists in the array
        const isSelected = selectedReactions?.some(el => el.unicode === reaction.unicode); // Check if Emoji is in Selected Array
        obj.unicode = reaction.unicode;
        obj.amount = reactions.filter(function (item) {
          return item.unicode === reaction.unicode;
        }).length;
        obj.selected = isSelected;
        if (
          reactionStatisticsBar.length > 3 &&
          isSelected &&
          !reactionStatisticsBar?.some(el => el.unicode === reaction.unicode) &&
          counter > 3
        ) {
          arr.unshift(obj);
        } else {
          arr.push(obj);
        }
        if (isSelected) counter++;
      }
    });
    setReactionStatistics(arr);
    if (isMobileView) {
      setReactionStatisticsBar(arr.slice(0, 2));
    } else {
      setReactionStatisticsBar(arr.slice(0, 4));
    }
  };

  return (
    <>
      <div className={styles.commentActions}>
        {isMobileView && <ReactionPicker handleReaction={handleReaction} isMobileView={isMobileView} />}
        {!isMobileView && (
          <Reactions
            user={user}
            reactions={reactions}
            handleReaction={handleReaction}
            username={username}
            localStorageCommentReactions={localStorageCommentReactions}
            reactionStatisticsBar={reactionStatisticsBar}
            reactionStatistics={reactionStatistics}
          />
        )}
        {!isMobileView && (
          <>
            <span className={styles.elapsedTime}>{t('comments.ago', { time: comment.since })}</span>
            <span className={styles.elapsedTime}>-</span>
          </>
        )}

        {isCanResolveComment && (
          <span className={styles.actionBtn} onClick={() => handleResolvedClick(comment.id)}>
            {t(comment.resolved ? 'general.reopen' : 'general.resolve')}
          </span>
        )}

        {isCanDeleteComment && (
          <span ref={deleteRef} className={styles.actionBtn} onClick={onDeleteClick} tabIndex={0} aria-label={t('general.delete')}>
            {t('general.delete')}
          </span>
        )}

        {isCanEditComment && (
          <span ref={editRef} className={styles.actionBtn} onClick={onEditComment} tabIndex={0} aria-label={t('general.edit')}>
            {t('general.edit')}
          </span>
        )}
        <span ref={replyRef} className={styles.actionBtn} onClick={onAddReplyFormTo} tabIndex={0} aria-label={t('general.reply')}>
          {t('general.reply')}
        </span>

        {!isMobileView && <ReactionPicker handleReaction={handleReaction} isMobileView={isMobileView} />}
      </div>
      {isMobileView && (
        <div className={styles.mobileEmojisContainer}>
          <Reactions
            user={user}
            reactions={reactions}
            handleReaction={handleReaction}
            reactionStatisticsBar={reactionStatisticsBar}
            reactionStatistics={reactionStatistics}
            isMobileView={isMobileView}
          />
        </div>
      )}

      {showNameInputField && user === null && (
        <NameField setUsername={setUsername} cachedReaction={cachedReaction} setShowNameInputField={setShowNameInputField} />
      )}
    </>
  );
};

export default CommentActions;
